import { AssistantMessage, PendingAction } from '../../../common/hooks/use-assistant-api.hook';

/**
 * The order a conversation actually happened in.
 *
 * Messages and proposals are stored as two lists, and the panel used to render
 * them as two lists — every message, then every proposal. That is right for a
 * proposal still waiting: it is the one thing on screen that does something,
 * and burying it in the transcript is how a vendor scrolls past a change they
 * meant to approve.
 *
 * It is wrong for a settled one. A change applied ten minutes ago stayed pinned
 * under the newest message, so asking about social accounts left "APPLIED:
 * change your primary colour" sitting below the answer as though it had just
 * happened. A resolved proposal is a record of what was decided, and a record
 * belongs where it occurred.
 *
 * ── Why time alone is not the answer ──────────────────────────────────────
 *
 * A proposal is stamped when the model CALLS the tool, which is in the middle
 * of the turn. The reply announcing it — "I've proposed changing your primary
 * colour to black" — is stored when the turn ENDS, a few seconds later. Sorting
 * both by their timestamps therefore puts the card above the sentence that
 * introduces it, which reads backwards.
 *
 * So a proposal is not placed by its own clock. It is anchored to the reply it
 * belongs to: the first assistant message at or after the moment it was
 * proposed. That is the sentence that announces it, and the card belongs
 * directly beneath.
 */

export type TimelineEntry =
    | { kind: 'message'; key: string; message: AssistantMessage }
    | { kind: 'action'; key: string; action: PendingAction };

export interface Timeline {
    /** Messages and settled proposals, in the order they should be read. */
    entries: TimelineEntry[];
    /** Proposals still waiting, which belong at the end whenever they were made. */
    pending: PendingAction[];
}

/** Milliseconds, or 0 for anything unparseable — an undated row still has to land somewhere. */
const time = (value: unknown): number => {
    const ms = new Date(String(value ?? '')).getTime();
    return Number.isFinite(ms) ? ms : 0;
};

export function conversationTimeline(
    messages: AssistantMessage[] = [],
    actions: PendingAction[] = [],
): Timeline {
    const pending = actions.filter((a) => a.status === 'pending');

    const settled = actions
        .filter((a) => a.status !== 'pending')
        .slice()
        // Two proposals in one turn keep the order they were made in, since
        // they will land against the same message.
        .sort((a, b) => time(a.proposedAt) - time(b.proposedAt));

    /**
     * One slot after each message, plus a trailing slot.
     *
     * Messages keep the order the server gave them rather than being re-sorted:
     * they are appended in sequence, and a message with a missing date should
     * stay where it was written rather than jumping to the front.
     */
    const after: PendingAction[][] = messages.map(() => []);
    const trailing: PendingAction[] = [];

    for (const action of settled) {
        const at = time(action.proposedAt);

        // The reply that announces it: the first thing the assistant said at or
        // after the tool was called.
        const anchor = messages.findIndex((m) => m.role !== 'vendor' && time(m.at) >= at);

        if (anchor === -1) {
            // Nothing was said afterwards — the turn is still the last thing in
            // the conversation, so the card is too.
            trailing.push(action);
        } else {
            after[anchor].push(action);
        }
    }

    const entries: TimelineEntry[] = [];

    messages.forEach((message, i) => {
        entries.push({ kind: 'message', key: `m${i}`, message });
        for (const action of after[i]) {
            entries.push({ kind: 'action', key: `a${action.actionId}`, action });
        }
    });

    for (const action of trailing) {
        entries.push({ kind: 'action', key: `a${action.actionId}`, action });
    }

    return { entries, pending };
}
